import { useHover } from '@react-aria/interactions';
import { createFilter } from '@shared/assets/icons';
import { DropdownMenu, Icon, Text } from '@shared/components';
import { IconData } from '@shared/components/icon/Icon';
import {
  makeElementClassNameFactory,
  makeRootClassName,
  StyleProps,
} from '@shared/utils';
import clsx from 'clsx';
import { ReactElement, ReactNode, useState } from 'react';
import {
  FILTER_GHOST_TRIGGER_CLASS_NAME,
  FILTER_MENU_COMPACT_CLASS_NAME,
  FILTER_MENU_CONTENT_CLASS_NAME,
} from './classNames';
import { DropdownMenuItem } from './components';
import { DropdownFilterOption } from './types';

type TriggerOptions = {
  label?: string | null;
  icon?: IconData | null;
  isGhost?: boolean;
};

export type DataTableFiltersProps = StyleProps & {
  options: DropdownFilterOption[];
  triggerOptions?: TriggerOptions;
  children?: ReactNode;
  isCompact?: boolean;
};

const ROOT = makeRootClassName('DataTableFilters');
const el = makeElementClassNameFactory(ROOT);

type TriggerProps = {
  options?: TriggerOptions;
};

const Trigger = (props: TriggerProps) => {
  const { hoverProps, isHovered } = useHover({});
  const label =
    props.options?.label === null ? null : props.options?.label ?? 'Filter';
  const icon =
    props.options?.icon === null ? null : props.options?.icon ?? createFilter;

  return (
    <DropdownMenu.Trigger>
      <button
        {...hoverProps}
        aria-label="Filter"
        className={
          props.options?.isGhost
            ? FILTER_GHOST_TRIGGER_CLASS_NAME
            : clsx(el`dropdown-trigger`, { 'is-hovered': isHovered })
        }
      >
        {label && <Text type="body-sm">Filter</Text>}
        {icon && (
          <Icon
            size={props.options?.isGhost ? 'small' : 'medium'}
            content={createFilter}
          />
        )}
      </button>
    </DropdownMenu.Trigger>
  );
};

const DEFAULT_FILTERS_PROPS = {} as const;

export default function DataTableFilters(
  props: DataTableFiltersProps
): ReactElement {
  const p = { ...DEFAULT_FILTERS_PROPS, ...props };
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={clsx(ROOT, p.className)}>
      <div className={el`actions`}>
        <DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
          <Trigger options={p.triggerOptions} />
          <DropdownMenu.Content
            side="bottom"
            align="start"
            className={clsx(FILTER_MENU_CONTENT_CLASS_NAME, {
              [FILTER_MENU_COMPACT_CLASS_NAME]: p.isCompact,
            })}
          >
            <Text
              isHeavy
              type="body-xs"
              className="p-[8px] uppercase text-gray-500"
            >
              Filter By
            </Text>
            {p.options.map((option) => (
              <DropdownMenuItem
                type={option.type}
                key={option.label}
                showSearch={option.showSearch}
                handleSearch={option.handleSearch}
                isLoading={option.isLoading}
                showMinMax={option.showMinMax}
                isNumberInput={option.isNumberInput}
                options={option}
                open={isOpen}
                setOpen={setIsOpen}
              />
            ))}
            {p.children}
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </div>
    </div>
  );
}
